import React from 'react'
import moment from 'moment'
import pluralize from 'pluralize'
import { Interval, EhrAppointmentSchedulingRuleDef, Subscription } from '@src/types'
import { DAY_NAME_MONTH_AND_DATE_WITH_LONG_YEAR_FORMAT } from '@src/config/momentFormat'
import { Feature, useFeatureFlag } from '@components'
import { usePaidCallCreditCount } from '@src/hooks/usePaidCallCreditCount.ts'
import { getPriceDescription } from '../../utils/utils'
import { SubscriptionField } from './SubscriptionField'

interface ProductSummaryFieldsProps {
  product: {
    ehrSchedulingRule: Pick<EhrAppointmentSchedulingRuleDef, 'count'>
    interval: Pick<Interval, 'suffix'>
  }
  price: number
  currentPeriodEndAt?: string
  commitmentEndAt?: string
  cancelAt?: string
  resumesAt?: string
  resetBillingCycleAnchorAt?: string
  isTrial: boolean
  mode: 'full' | 'compact'
  fields: Fields[]
  subscription?: Pick<Subscription, 'id'>
}

export enum Fields {
  billedAmount,
  commitmentEnds,
  subscriptionEnds,
  nextBillingDate,
  resumesAt,
  discountedRate,
  daysLeft,
  callUsed,
}

export const ProductSummaryFields = ({
  product,
  price: amount,
  currentPeriodEndAt,
  commitmentEndAt,
  cancelAt: cancelAtString,
  resumesAt: resumesAtString,
  resetBillingCycleAnchorAt,
  isTrial,
  mode = 'full',
  fields,
  subscription,
}: ProductSummaryFieldsProps) => {
  const nextBillingDate = moment(resetBillingCycleAnchorAt || currentPeriodEndAt)
  const billingDateIsSameAsCancelDate = nextBillingDate.isSame(cancelAtString, 'day')
  const subscriptionFieldSize = mode === 'full' ? 'normal' : 'small'
  const resumesAt = resumesAtString ? moment(resumesAtString) : null
  const videoCallCreditsEnabled = useFeatureFlag(Feature.VideoCallCredits)
  const paidCallCreditCount = usePaidCallCreditCount(subscription)

  const billedAmountField = () => {
    return (
      <SubscriptionField name="Billed amount" value={`$${amount}`} size={subscriptionFieldSize} />
    )
  }

  const commitmentEndsField = () => {
    if (!commitmentEndAt) {
      return null
    }

    const commitmentEndsAt = moment(commitmentEndAt)
    const daysLeft = commitmentEndsAt.diff(moment(), 'days')

    if (daysLeft <= 0) {
      return null
    }

    return (
      <SubscriptionField
        name="Commitment ends"
        value={commitmentEndsAt.format(DAY_NAME_MONTH_AND_DATE_WITH_LONG_YEAR_FORMAT)}
        secondaryValue={`(${pluralize('day', daysLeft, true)} left)`}
        size={subscriptionFieldSize}
      />
    )
  }

  const subscriptionEndsField = () => {
    if (!cancelAtString) {
      return null
    }

    const cancelAt = moment(cancelAtString)

    return (
      <SubscriptionField
        name="Subscription ends"
        value={cancelAt.format(DAY_NAME_MONTH_AND_DATE_WITH_LONG_YEAR_FORMAT)}
        secondaryValue={`(${pluralize('day', cancelAt.diff(moment(), 'days'), true)} left)`}
        size={subscriptionFieldSize}
      />
    )
  }

  const nextBillingDateField = () => {
    if (billingDateIsSameAsCancelDate || (resumesAt && resumesAt.isAfter(nextBillingDate))) {
      return null
    }

    return (
      <SubscriptionField
        name="Next billing date"
        value={nextBillingDate.format(DAY_NAME_MONTH_AND_DATE_WITH_LONG_YEAR_FORMAT)}
        size={subscriptionFieldSize}
      />
    )
  }

  const daysLeftField = () => {
    if (!isTrial) {
      return null
    }

    const daysLeft = moment(currentPeriodEndAt).diff(moment(), 'days')

    return (
      <SubscriptionField
        name="Days left"
        value={daysLeft.toString()}
        size={subscriptionFieldSize}
      />
    )
  }

  const resumesAtField = () => {
    if (!resumesAt) {
      return null
    }

    return (
      <SubscriptionField
        name="Subscription resumes"
        value={resumesAt.format(DAY_NAME_MONTH_AND_DATE_WITH_LONG_YEAR_FORMAT)}
        size={subscriptionFieldSize}
      />
    )
  }

  const discountedRateField = () => {
    return (
      <SubscriptionField
        name="Discounted rate"
        value={getPriceDescription(amount, product.interval)}
        size={subscriptionFieldSize}
      />
    )
  }

  const callUsedField = () => {
    if (!videoCallCreditsEnabled) {
      return
    }

    const schedulingRuleCount = product.ehrSchedulingRule.count
    if (!schedulingRuleCount) {
      return
    }

    // If the user has more paid call credits than their monthly billing period allowance,
    // then we can assume that any calls used consumed credits granted outside of the current
    // billing period. In which case, they haven't started to use any of this billing period's
    // allowance yet, so 'Calls used' would effectively be zero.
    //
    // e.g. 2 calls/month; 3 credits remaining
    //      => used 1 credit from previous period (2 + 2 - 1 = 3)
    //      => used 0 credits from this period (3 > 2)
    //      => Calls used: 0 (since we are showing details for the current billing period)
    //
    const callsUsedCount = Math.max(schedulingRuleCount - paidCallCreditCount, 0).toString()

    return (
      <SubscriptionField
        name="Calls used"
        value={callsUsedCount}
        secondaryValue={`${paidCallCreditCount} ${pluralize(
          'Calls',
          paidCallCreditCount,
        )} Remaining`}
        size={subscriptionFieldSize}
      />
    )
  }

  const componentByField = {
    [Fields.billedAmount]: billedAmountField,
    [Fields.commitmentEnds]: commitmentEndsField,
    [Fields.subscriptionEnds]: subscriptionEndsField,
    [Fields.nextBillingDate]: nextBillingDateField,
    [Fields.resumesAt]: resumesAtField,
    [Fields.discountedRate]: discountedRateField,
    [Fields.daysLeft]: daysLeftField,
    [Fields.callUsed]: callUsedField,
  }

  const renderedFields = fields
    .map((field) => ({ field, component: componentByField[field]() }))
    .filter(
      (
        item,
      ): item is {
        field: Fields
        component: React.ReactElement
      } => !!item.component,
    )

  return (
    <>
      {renderedFields.map(({ field, component }, index) => (
        <React.Fragment key={field}>
          {React.cloneElement(component as React.ReactElement<{ showDivider?: boolean }>, {
            showDivider: index < renderedFields.length - 1,
          })}
        </React.Fragment>
      ))}
    </>
  )
}
